import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, productsApi } from '@/lib/api-client';
import type { PaginatedResponse } from '@supernova/shared';
import { useVendorPriceHistory } from './use-products';
import {
  FETCH_WINDOW,
  parseMovements,
  parsePricePoints,
  windowStart,
  type PriceSeriesKind,
} from '@/lib/product-intelligence';

/**
 * The same params the Vendors tab's price-history panel uses for its "all
 * vendors" list, so the Intelligence tab reads that warm cache instead of
 * fetching the same rows twice. The products service ignores from/to on this
 * route; the tab narrows to the year itself.
 */
const QUOTED_PARAMS = { perPage: 1000 };

/** Quoted series: `vendor_price_history` via the products service (admin+). */
export function useQuotedPriceHistory(productId: string | undefined, enabled = true) {
  return useVendorPriceHistory(enabled ? productId : undefined, QUOTED_PARAMS);
}

/**
 * Paid series: `purchase_order_items.cost_per_unit` via the dashboard API
 * (admin+). Tenant-wide, like the quoted series — the mobile app asks the same.
 */
export function usePaidPriceHistory(productId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['products', productId, 'vendor-paid-history', 'year'],
    queryFn: () => {
      const now = new Date();
      return api.get<PaginatedResponse<any>>(`/products/${productId}/vendor-paid-history`, {
        from: windowStart(FETCH_WINDOW, now).toISOString(),
        to: now.toISOString(),
        perPage: 500,
      });
    },
    enabled: enabled && !!productId,
  });
}

/** One of the two price series, parsed and sorted oldest first. */
export function usePriceSeriesPoints(productId: string | undefined, kind: PriceSeriesKind, enabled = true) {
  const quoted = useQuotedPriceHistory(productId, enabled && kind === 'quoted');
  const paid = usePaidPriceHistory(productId, enabled && kind === 'paid');
  const q = kind === 'paid' ? paid : quoted;
  const points = useMemo(() => parsePricePoints(q.data?.data), [q.data]);
  return { points, isLoading: q.isLoading, isError: q.isError, error: q.error, refetch: q.refetch };
}

/**
 * First page of one product's stock-movement ledger at one location, newest
 * first. Rides the products service's recent-activity feed with its
 * `productId` filter (manager+, location access enforced server-side). 100 is
 * the endpoint's page maximum.
 */
export function useProductStockMovements(productId: string | undefined, locationId: string | undefined) {
  const query = useQuery({
    queryKey: ['products', productId, 'stock-movements', locationId],
    queryFn: () =>
      productsApi.get<PaginatedResponse<any>>('/products/stock-movements', {
        locationId,
        productId,
        page: 1,
        perPage: 100,
      }),
    enabled: !!productId && !!locationId,
  });
  const movements = useMemo(
    () => (productId ? parseMovements(query.data?.data, productId) : []),
    [query.data, productId],
  );
  const total = (query.data as any)?.pagination?.total as number | undefined;
  return { ...query, movements, total };
}
